import type { HelpGuide } from '../types';

export const vatGuide: HelpGuide = {
  key: 'vat',
  title: 'ภาษีมูลค่าเพิ่ม (VAT)',
  subtitle: 'เมนู: รายงานและภาษี ▸ ภาษีมูลค่าเพิ่ม',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/vat/คู่มือการใช้งาน_ภาษีมูลค่าเพิ่ม_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ภาษีมูลค่าเพิ่ม (VAT)” ใช้สรุปภาษีขาย (Output VAT) และภาษีซื้อ (Input VAT) ของแต่ละงวด (เดือน) เพื่อคำนวณ ภาษีต้องชำระ แล้วจัดทำและบันทึกการยื่นแบบ ภ.พ.30 พร้อมติดตามภาษีซื้อที่ยังพักรอเคลม (รอใบกำกับภาษีต้นฉบับ) และดูการยื่นแยกตามสาขาที่จดทะเบียนภาษีมูลค่าเพิ่ม',
        },
        {
          kind: 'callout',
          text: 'หลักการ VAT: ภาษีขาย (Output VAT) = VAT 7% ที่เก็บจากลูกค้าตอนขาย · ภาษีซื้อ (Input VAT) = VAT ที่จ่ายตอนซื้อและขอคืนได้ · ภาษีต้องชำระ = ภาษีขาย − ภาษีซื้อ (รายการที่ (2)−(4) ของแบบ ภ.พ.30) ยื่นและชำระภายในวันที่ 15 ของเดือนถัดไป',
        },
        { kind: 'para', text: 'หน้าจอเรียงเป็นการ์ดตามลำดับ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'การ์ดสรุปยอด (KPI) — ',
              text: 'ยอดขาย/ภาษีขาย/ยอดซื้อ/ภาษีต้องชำระของงวดที่เลือก',
            },
            { lead: 'รายงานภาษีขาย (Output VAT) — ', text: 'รายงานภาษีขายรายใบกำกับของงวด' },
            { lead: 'รายงานภาษีซื้อ (Input VAT) — ', text: 'รายงานภาษีซื้อรายใบกำกับของงวด' },
            { lead: 'สรุปการคำนวณภาษี ภ.พ.30 — ', text: 'สรุปยอดสำหรับยื่น + ขั้นตอนการยื่น' },
            {
              lead: 'ภาษีซื้อยังไม่ถึงกำหนด — ',
              text: 'รายการภาษีซื้อพักรอเคลม (รอใบกำกับภาษีต้นฉบับ) + ป้ายอายุ',
            },
            { lead: 'แยกตามสาขา — ', text: 'ภาษีและสถานะการยื่นแยกตามสาขา (tax-branch)' },
            { lead: 'การยื่นภ.พ.30 ปี — ', text: 'ประวัติการยื่นทั้งปี (12 งวด)' },
          ],
        },
        {
          kind: 'image',
          src: '/help/vat/01-overview.png',
          caption:
            'ภาพรวม VAT — การ์ด KPI ยอดขาย / ภาษีขาย / ยอดซื้อ / ภาษีต้องชำระ ของงวดที่เลือก',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        {
          kind: 'para',
          text: 'ด้านบนสุดมี ตัวเลือกงวด (งวด: ม.ค. 2026 … ธ.ค. 2026) ใช้เลือกเดือนเพื่อให้ข้อมูลทุกการ์ดด้านล่างเปลี่ยนตาม และปุ่มมุมขวาบน: ส่งออกแบบ ภ.พ.30 และ ยื่นภ.พ.30 (ปุ่มหลัก)',
        },
        { kind: 'para', text: 'การ์ดสรุปยอด (KPI) 4 ใบ:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ยอดขายรวม (ไม่รวม VAT) — ',
              text: 'ฐานยอดขายของงวด พร้อมจำนวนใบกำกับ',
            },
            { lead: 'ภาษีขาย (Output VAT) — ', text: 'ภาษีขายรวมของงวด (VAT 7%)' },
            { lead: 'ยอดซื้อรวม (ไม่รวม VAT) — ', text: 'ฐานยอดซื้อของงวด พร้อมจำนวนใบกำกับ' },
            {
              lead: 'ภาษีต้องชำระ — ',
              text: 'ภาษีขาย − ภาษีซื้อ พร้อมวันกำหนดยื่นของงวด',
            },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มที่ใช้บ่อย: CSV (มุมการ์ดรายงานภาษีขาย/ภาษีซื้อ) ส่งออกรายงานเป็นไฟล์ · ยื่นแยกสาขา (การ์ดแยกตามสาขา) · ดูรายละเอียด (ตารางการยื่นรายเดือน) ส่วนปุ่มในรายการภาษีซื้อพักรอ ได้แก่ รับใบกำกับภาษีต้นฉบับแล้ว · ดูสายอนุมัติ · ตัดจำหน่าย',
        },
      ],
    },
    {
      id: 'output-register',
      heading: '3. รายงานภาษีขาย (Output VAT)',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด รายงานภาษีขาย (Output VAT) แสดงภาษีขายรายใบกำกับของงวดที่เลือก คอลัมน์: เลขที่ใบกำกับ · วันที่ · ผู้ซื้อ · เลขผู้เสียภาษี · มูลค่า · VAT และมีแถวรวมท้ายตาราง (รวม | มูลค่า | VAT) ใช้ปุ่ม CSV เพื่อส่งออกรายงานเก็บไว้',
        },
        {
          kind: 'image',
          src: '/help/vat/02-output-register.png',
          caption: 'รายงานภาษีขาย (Output VAT) — รายใบกำกับ พร้อมมูลค่า/VAT และยอดรวม',
        },
      ],
    },
    {
      id: 'input-register',
      heading: '4. รายงานภาษีซื้อ (Input VAT)',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด รายงานภาษีซื้อ (Input VAT) แสดงภาษีซื้อรายใบกำกับของงวดที่เลือก คอลัมน์: เลขที่ใบกำกับ · วันที่ · ผู้ขาย · เลขผู้เสียภาษี · มูลค่า · VAT พร้อมแถวรวมท้ายตาราง ใช้คู่กับรายงานภาษีขายเพื่อกระทบยอดก่อนยื่น และส่งออกได้ด้วยปุ่ม CSV',
        },
        {
          kind: 'image',
          src: '/help/vat/03-input-register.png',
          caption: 'รายงานภาษีซื้อ (Input VAT) — รายใบกำกับ พร้อมมูลค่า/VAT และยอดรวม',
        },
      ],
    },
    {
      id: 'ppo30',
      heading: '5. สรุปการคำนวณภาษี ภ.พ.30 และการยื่น',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด สรุปการคำนวณภาษี ภ.พ.30 รวมยอดทั้งงวดเป็นรายการตามแบบ: (1) ยอดขายที่ต้องเสียภาษี · (2) ภาษีขาย · (3) ยอดซื้อที่ขอภาษีคืน · (4) ภาษีซื้อ · ภาษีต้องชำระ (2−4) มุมขวาบนของการ์ดมีป้ายสถานะของงวด ก่อนยื่นจะแสดง รอยื่น เมื่อยื่นแล้วเปลี่ยนเป็น ยื่นแล้ว (หรือ ยื่นเพิ่มเติม กรณียื่นแก้ไขภายหลัง) ด้านข้างมีการ์ด ขั้นตอนการยื่น เป็นเช็กลิสต์ความคืบหน้า: ตรวจสอบใบกำกับภาษีทั้งหมด → กระทบยอดกับสมุดรายวันขาย/ซื้อ → อนุมัติโดย ผจก. บัญชี → ยื่นแบบทางอินเทอร์เน็ต → ชำระเงินผ่านธนาคาร',
        },
        {
          kind: 'para',
          text: 'เมื่อตรวจยอดถูกต้องแล้ว กดปุ่ม ยื่นภ.พ.30 (มุมขวาบน) เพื่อบันทึกการยื่นของงวด ระบบจะคำนวณภาษีขาย/ภาษีซื้อ/ภาษีต้องชำระ และสร้างรายการการยื่น สถานะจะเปลี่ยนเป็น ยื่นแล้ว ปุ่ม ส่งออกแบบ ภ.พ.30 ใช้ส่งออกแบบสำหรับยื่นต่อกรมสรรพากร',
        },
        {
          kind: 'image',
          src: '/help/vat/04-ppo30-summary.png',
          caption: 'สรุปการคำนวณภาษี ภ.พ.30 (1)–(4) + ภาษีต้องชำระ และการ์ดขั้นตอนการยื่น',
        },
      ],
    },
    {
      id: 'suspense',
      heading: '6. ภาษีซื้อยังไม่ถึงกำหนด (พักรอเคลม)',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด ภาษีซื้อยังไม่ถึงกำหนด (รอใบกำกับภาษีต้นฉบับ) แสดงภาษีซื้อที่ยังพักรอเคลม คอลัมน์: เลขที่ใบสำคัญ · วันที่บิล · ผู้ขาย · ภาษีซื้อ (รอ) · ครบกำหนดขอคืน · วันคงเหลือ · อายุ · ปุ่มจัดการ ป้ายอายุแบ่งเป็น ปกติ / ใกล้ครบ / เกินกำหนด เพื่อเตือนกำหนดเคลม (กฎ 6 เดือน)',
        },
        {
          kind: 'para',
          text: 'เมื่อได้รับใบกำกับภาษีต้นฉบับแล้ว กดปุ่ม รับใบกำกับภาษีต้นฉบับแล้ว แถวจะขยายให้กรอก เลขที่ใบกำกับภาษี · วันที่ใบกำกับ · วันที่รับเอกสาร แล้วกด ยืนยันรับใบกำกับ ระบบจะรับรู้ภาษีซื้อให้นำมาเคลมในงวด ส่วนรายการที่ เกินกำหนด และเคลมไม่ได้ ให้ใช้ปุ่ม ตัดจำหน่าย (ดูหัวข้อถัดไป)',
        },
        {
          kind: 'image',
          src: '/help/vat/05-suspense-worklist.png',
          caption:
            'ภาษีซื้อยังไม่ถึงกำหนด — รายการพักรอเคลม พร้อมป้ายอายุ ปกติ / ใกล้ครบ / เกินกำหนด',
        },
      ],
    },
    {
      id: 'writeoff',
      heading: '7. การตัดจำหน่าย VAT ซื้อ (เกินกำหนดเคลม)',
      blocks: [
        {
          kind: 'para',
          text: 'สำหรับภาษีซื้อที่ เกินกำหนด (เกิน 6 เดือน เคลมไม่ได้ตามมาตรา 82/5) กดปุ่ม ตัดจำหน่าย ที่แถวนั้น ระบบเปิดหน้าต่าง ตัดจำหน่าย VAT ซื้อ (เกินกำหนดเคลม) แสดงเลขที่ใบสำคัญและชื่อผู้ขาย พร้อมช่อง: ใบสำคัญ (อ่านอย่างเดียว) · ยอด VAT พักรอเคลม (อ่านอย่างเดียว) · วันที่ตัดจำหน่าย · เหตุผล',
        },
        {
          kind: 'para',
          text: 'กรอกวันที่ตัดจำหน่ายและเหตุผล (เช่น ใบกำกับภาษีเกิน 6 เดือน เคลมไม่ได้ตามมาตรา 82/5) แล้วกด ตัดจำหน่าย ระบบจะสร้าง “คำขอตัดจำหน่าย” สถานะ ร่าง เพื่อเข้าสู่สายอนุมัติ (ยังไม่ลงบัญชีจนกว่าจะอนุมัติครบ)',
        },
        {
          kind: 'image',
          src: '/help/vat/06-writeoff-modal.png',
          caption:
            'ตัดจำหน่าย VAT ซื้อ (เกินกำหนดเคลม) — ใบสำคัญ, ยอด VAT, วันที่ตัดจำหน่าย, เหตุผล',
        },
      ],
    },
    {
      id: 'approval',
      heading: '8. สายอนุมัติการตัดจำหน่าย VAT',
      blocks: [
        {
          kind: 'para',
          text: 'รายการที่มีคำขอตัดจำหน่ายค้างอยู่จะมีปุ่ม ดูสายอนุมัติ เปิดหน้าต่าง สายอนุมัติการตัดจำหน่าย VAT แสดงสายอนุมัติแบบสี่ตา (ผู้จัดทำ → ผู้สอบทาน → ผู้อนุมัติ) ตามเครื่องมืออนุมัติกลางของระบบ พร้อมไทม์ไลน์แต่ละขั้น (ลำดับขั้น · บทบาท · ผู้อนุมัติ · เวลา)',
        },
        {
          kind: 'para',
          text: 'ถ้าคำขออยู่สถานะ ร่าง หรือ ส่งกลับ ผู้จัดทำกดปุ่ม ส่งอนุมัติ เพื่อเข้าสายอนุมัติ ถ้ารอพิจารณาจะมีปุ่ม พิจารณา (เห็นเฉพาะผู้มีสิทธิอนุมัติขั้นปัจจุบัน) ให้เลือก อนุมัติ / ส่งกลับ / ปฏิเสธ เมื่ออนุมัติขั้นสุดท้ายระบบจะลงใบสำคัญตัดจำหน่ายอัตโนมัติ',
        },
        {
          kind: 'image',
          src: '/help/vat/07-approval-panel.png',
          caption:
            'สายอนุมัติการตัดจำหน่าย VAT — ไทม์ไลน์ผู้จัดทำ-สอบทาน-อนุมัติ และปุ่มส่งอนุมัติ/พิจารณา',
        },
      ],
    },
    {
      id: 'branch',
      heading: '9. แยกภาษีตามสาขา (tax-branch)',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด แยกตามสาขา แสดงภาษีของแต่ละสาขาที่จดทะเบียนภาษีมูลค่าเพิ่ม คอลัมน์: รหัสสาขา · สาขา · ภาษีขาย · ภาษีซื้อ · ภาษีต้องชำระ · สถานะการยื่น และแถวรวมทุกสาขาท้ายตาราง ป้ายสถานะการยื่นต่อสาขา ได้แก่ ยื่นแล้ว / ยื่นเพิ่มเติม / ร่าง / ยังไม่ยื่น / ไม่ระบุสาขา',
        },
        {
          kind: 'para',
          text: 'กรณีต้องยื่น ภ.พ.30 แยกรายสาขา กดปุ่ม ยื่นแยกสาขา ระบบจะสร้างการยื่นหนึ่งใบต่อสาขาที่มีกิจกรรมภาษีในงวดนั้น (สาขาที่ยังไม่ระบุจะถูกข้าม)',
        },
        {
          kind: 'image',
          src: '/help/vat/08-branch-breakdown.png',
          caption: 'แยกภาษีตามสาขา (tax-branch) — ภาษีขาย/ซื้อ/ต้องชำระ ต่อสาขา และสถานะการยื่น',
        },
      ],
    },
    {
      id: 'filings',
      heading: '10. ประวัติการยื่น ภ.พ.30 ทั้งปี',
      blocks: [
        {
          kind: 'para',
          text: 'การ์ด การยื่นภ.พ.30 ปี (YYYY) แสดงตาราง 12 งวดของปี คอลัมน์: งวด · ภาษีขาย · ภาษีซื้อ · ภาษีต้องชำระ · สถานะ · วันที่ยื่น · ปุ่มดูรายละเอียด สถานะแต่ละงวด ได้แก่ ยื่นแล้ว / ยื่นเพิ่มเติม / ร่าง / ยังไม่ยื่น',
        },
        {
          kind: 'para',
          text: 'กดปุ่ม ดูรายละเอียด ที่งวดใด ระบบจะเปลี่ยนตัวเลือกงวดไปงวดนั้น และโหลดข้อมูลทุกการ์ดด้านบน (รายงานภาษีขาย/ซื้อ, สรุป ภ.พ.30, รายการพักรอ) ใหม่ตามงวดที่เลือก ใช้สลับดูรายงาน VAT ระหว่างเดือนได้สะดวก',
        },
        {
          kind: 'image',
          src: '/help/vat/09-filings-history.png',
          caption:
            'ประวัติการยื่น ภ.พ.30 ทั้งปี — ยื่นแล้ว / ยื่นเพิ่มเติม / ยังไม่ยื่น ราย 12 งวด',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะและป้ายต่าง ๆ',
      blocks: [
        {
          kind: 'para',
          text: 'สถานะการยื่น ภ.พ.30 (ป้ายบนการ์ดสรุป ภ.พ.30 และในตารางรายเดือน/แยกสาขา):',
        },
        {
          kind: 'table',
          head: ['ป้ายสถานะ', 'ความหมาย'],
          rows: [
            ['รอยื่น', 'ป้ายบนการ์ดสรุป ภ.พ.30 ก่อนยื่น (งวดนี้ยังไม่ได้ยื่น)'],
            ['ร่าง', 'ในตารางรายเดือน/แยกสาขา: สร้างรายการการยื่นแล้วแต่ยังไม่ได้ยื่นจริง'],
            ['ยื่นแล้ว', 'ยื่นแบบต่อกรมสรรพากรเรียบร้อยแล้ว'],
            ['ยื่นเพิ่มเติม', 'ยื่นเพิ่มเติม/แก้ไขหลังจากยื่นครั้งแรก'],
            ['ยังไม่ยื่น', 'งวด/สาขานี้ยังไม่มีการยื่น'],
            ['ไม่ระบุสาขา', 'รายการที่ยังไม่ได้ระบุสาขา (เฉพาะตารางแยกสาขา)'],
          ],
        },
        {
          kind: 'para',
          text: 'ป้ายอายุของภาษีซื้อพักรอเคลม (กำหนดเคลม 6 เดือน):',
        },
        {
          kind: 'table',
          head: ['ป้ายอายุ', 'ความหมาย'],
          rows: [
            ['ปกติ', 'ยังเหลือเวลาเคลมมากกว่า 30 วัน'],
            ['ใกล้ครบ', 'เหลือเวลาเคลม 0–30 วัน ควรเร่งติดตามใบกำกับ'],
            ['เกินกำหนด', 'เลยกำหนดขอคืนแล้ว เคลมไม่ได้ ต้องตัดจำหน่าย'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ภาษีต้องชำระคำนวณอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ภาษีต้องชำระ = ภาษีขาย (Output VAT) − ภาษีซื้อ (Input VAT) = รายการ (2)−(4) ของแบบ ภ.พ.30',
            },
          ],
        },
        { kind: 'para', text: 'ภาษีซื้อที่ยังไม่มีใบกำกับต้นฉบับ ทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ดูที่การ์ด ภาษีซื้อยังไม่ถึงกำหนด เมื่อได้ใบกำกับให้กด รับใบกำกับภาษีต้นฉบับแล้ว แล้วกรอกเลขที่/วันที่ใบกำกับเพื่อรับรู้ภาษีซื้อ',
            },
          ],
        },
        { kind: 'para', text: 'ถ้าภาษีซื้อเกิน 6 เดือน เคลมไม่ได้แล้ว?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ใช้ปุ่ม ตัดจำหน่าย ที่แถว เกินกำหนด สร้างคำขอตัดจำหน่าย แล้วผ่านสายอนุมัติ (ผู้จัดทำ-สอบทาน-อนุมัติ) จึงจะลงบัญชี',
            },
          ],
        },
        { kind: 'para', text: 'ต้องการยื่นแยกรายสาขาทำได้ไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ได้ ใช้ปุ่ม ยื่นแยกสาขา ในการ์ด แยกตามสาขา ระบบสร้างการยื่นหนึ่งใบต่อสาขาที่มีกิจกรรมภาษีในงวด',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ภ.พ.30 — ',
              text: 'แบบแสดงรายการภาษีมูลค่าเพิ่ม ยื่นและชำระภายในวันที่ 15 ของเดือนถัดไป',
            },
            {
              lead: 'ภาษีขาย (Output VAT) — ',
              text: 'VAT 7% ที่เรียกเก็บจากลูกค้าตอนขายสินค้า/บริการ',
            },
            {
              lead: 'ภาษีซื้อ (Input VAT) — ',
              text: 'VAT ที่จ่ายตอนซื้อและขอเครดิต/คืนได้',
            },
            {
              lead: 'ภาษีต้องชำระ — ',
              text: 'ผลต่างภาษีขาย − ภาษีซื้อ ที่ต้องนำส่งกรมสรรพากร',
            },
            {
              lead: 'พักรอเคลม — ',
              text: 'ภาษีซื้อที่ยังไม่รับรู้ เพราะรอใบกำกับภาษีต้นฉบับ (มีกำหนดเคลม 6 เดือน)',
            },
            {
              lead: 'ตัดจำหน่าย VAT ซื้อ — ',
              text: 'การตัดภาษีซื้อที่เกินกำหนดเคลมออก (เคลมไม่ได้ตามมาตรา 82/5) ผ่านสายอนุมัติ',
            },
            {
              lead: 'tax-branch (สาขาภาษี) — ',
              text: 'สาขาที่จดทะเบียนภาษีมูลค่าเพิ่ม ใช้แยกยื่น/แยกคำนวณภาษีรายสาขา',
            },
          ],
        },
      ],
    },
  ],
};
